import React, { useEffect, useState } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
	getTemplateCategories,
	createTemplateCategory,
	updateTemplateCategory,
	deleteTemplateCategory,
} from "../../../../api/template-category";
import {
	Button,
	TextField,
	FormControlLabel,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
	TEMPLATE_CATEGORY_ICON_OPTIONS,
	getTemplateCategoryIconOption,
} from "../../../../utils/templateCategoryIcons";

const emptyForm = { name: "", nameEn: "", icon: "label", sortOrder: 0, active: true };

export default function TemplateCategoriesPage() {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [deleteId, setDeleteId] = useState(null);
	const [refresh, setRefresh] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		getTemplateCategories({ all: true })
			.then((res) => {
				if (cancelled) return;
				setItems(Array.isArray(res.data) ? res.data : []);
			})
			.catch((err) => {
				if (!cancelled) {
					setItems([]);
					alertError({ error: err, customMessage: "No se pudieron cargar los tipos." });
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refresh]);

	const openCreate = () => {
		setEditingId(null);
		setForm({ ...emptyForm, sortOrder: items.length });
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditingId(row._id);
		setForm({
			name: row.name || "",
			nameEn: row.nameEn || "",
			icon: String(row.icon || "label"),
			sortOrder: Number(row.sortOrder) || 0,
			active: row.active !== false,
		});
		setDialogOpen(true);
	};

	const handleSave = () => {
		if (!String(form.name || "").trim()) {
			alertError({ customMessage: "El nombre es obligatorio." });
			return;
		}
		const payload = {
			name: String(form.name).trim(),
			nameEn: String(form.nameEn || "").trim(),
			icon: String(form.icon || "label").trim() || "label",
			sortOrder: Number(form.sortOrder) || 0,
			active: Boolean(form.active),
		};
		const req = editingId
			? updateTemplateCategory(editingId, payload)
			: createTemplateCategory(payload);
		req
			.then(() => {
				alertSuccess({ customMessage: editingId ? "Tipo actualizado." : "Tipo creado." });
				setDialogOpen(false);
				setRefresh((n) => n + 1);
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo guardar." });
			});
	};

	const handleDelete = () => {
		const id = deleteId;
		if (!id) return;
		deleteTemplateCategory(id)
			.then(() => {
				alertSuccess({ customMessage: "Tipo eliminado." });
				setRefresh((n) => n + 1);
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo eliminar." });
			})
			.finally(() => setDeleteId(null));
	};

	return (
		<>
			<Card>
				<CardHeader title="Tipos de plantilla">
					<CardHeaderToolbar>
						<Button variant="contained" color="primary" onClick={openCreate}>
							Nuevo tipo
						</Button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<div style={{ color: "#64748b", fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
						Define los tipos (p. ej. Evento, Formación) y asígnalos a cada plantilla en su ficha (puedes
						elegir varios). En el portal los usuarios podrán filtrar por ellos.
					</div>
					{loading ? (
						<div>Cargando…</div>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Orden</TableCell>
										<TableCell>Icono</TableCell>
										<TableCell>Nombre (ES)</TableCell>
										<TableCell>Nombre (EN)</TableCell>
										<TableCell>Estado</TableCell>
										<TableCell align="right">Acciones</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{items.length === 0 ? (
										<TableRow>
											<TableCell colSpan={6} style={{ color: "#64748b" }}>
												No hay tipos todavía. Crea el primero.
											</TableCell>
										</TableRow>
									) : (
										items.map((row) => (
											<TableRow key={row._id}>
												<TableCell>{row.sortOrder}</TableCell>
												<TableCell>
													{(() => {
														const Icon = getTemplateCategoryIconOption(row.icon).Icon;
														return <Icon style={{ fontSize: 18, color: "#475569" }} />;
													})()}
												</TableCell>
												<TableCell style={{ fontWeight: 700 }}>{row.name}</TableCell>
												<TableCell>{row.nameEn || "—"}</TableCell>
												<TableCell>{row.active !== false ? "Activo" : "Inactivo"}</TableCell>
												<TableCell align="right">
													<Button size="small" onClick={() => openEdit(row)} startIcon={<EditIcon />}>
														Editar
													</Button>
													<Button
														size="small"
														color="secondary"
														onClick={() => setDeleteId(row._id)}
														startIcon={<DeleteIcon />}
													>
														Eliminar
													</Button>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardBody>
			</Card>

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>{editingId ? "Editar tipo" : "Nuevo tipo"}</DialogTitle>
				<DialogContent dividers>
					<TextField
						label="Nombre (ES)"
						fullWidth
						margin="normal"
						variant="outlined"
						value={form.name}
						onChange={(e) => {
							const name = e.target.value;
							setForm((f) => ({ ...f, name }));
						}}
					/>
					<TextField
						label="Nombre (EN)"
						fullWidth
						margin="normal"
						variant="outlined"
						value={form.nameEn}
						onChange={(e) => {
							const nameEn = e.target.value;
							setForm((f) => ({ ...f, nameEn }));
						}}
						helperText="Opcional. Si está vacío, el portal en inglés usa el nombre ES."
					/>
					<div style={{ marginTop: 12, marginBottom: 8 }}>
						<div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
							Icono
						</div>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))",
								gap: 8,
								maxHeight: 260,
								overflowY: "auto",
								padding: 8,
								border: "1px solid #e2e8f0",
								borderRadius: 12,
								background: "#f8fafc",
							}}
						>
							{TEMPLATE_CATEGORY_ICON_OPTIONS.map(({ id, label, Icon }) => {
								const selected = String(form.icon || "label") === id;
								return (
									<Tooltip key={id} title={label} placement="top">
										<button
											type="button"
											onClick={() => setForm((f) => ({ ...f, icon: id }))}
											style={{
												appearance: "none",
												width: "100%",
												aspectRatio: "1",
												borderRadius: 10,
												border: selected ? "2px solid #7c3aed" : "1px solid #e2e8f0",
												background: selected ? "#f5f3ff" : "#fff",
												color: selected ? "#6d28d9" : "#475569",
												cursor: "pointer",
												display: "grid",
												placeItems: "center",
												padding: 0,
												boxShadow: selected ? "0 0 0 2px rgba(124,58,237,0.15)" : "none",
											}}
										>
											<Icon style={{ fontSize: 20 }} />
										</button>
									</Tooltip>
								);
							})}
						</div>
						<div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>
							{getTemplateCategoryIconOption(form.icon).label}
						</div>
					</div>
					<TextField
						label="Orden"
						type="number"
						fullWidth
						margin="normal"
						variant="outlined"
						value={form.sortOrder}
						onChange={(e) => {
							const sortOrder = Number(e.target.value) || 0;
							setForm((f) => ({ ...f, sortOrder }));
						}}
					/>
					<FormControlLabel
						control={
							<Switch
								color="primary"
								checked={Boolean(form.active)}
								onChange={(e) => {
									const active = e.target.checked;
									setForm((f) => ({ ...f, active }));
								}}
							/>
						}
						label="Activo (visible en el portal)"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
					<Button color="primary" variant="contained" onClick={handleSave}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				title="¿Eliminar este tipo?"
				open={Boolean(deleteId)}
				setOpen={(open) => {
					if (!open) setDeleteId(null);
				}}
				onConfirm={handleDelete}
			/>
		</>
	);
}
